<template>
  <div class="stock-in">
    <div class="page-header">
      <h2>库存入库</h2>
    </div>
    
    <a-form
      :model="form"
      @finish="handleSubmit"
      layout="vertical"
    >
      <a-card title="入库信息" class="form-card">
        <a-row :gutter="16">
          <a-col :span="8">
            <a-form-item label="入库类型" name="reasonType">
              <a-select
                v-model:value="form.reasonType"
                placeholder="请选择入库类型"
              >
                <a-select-option value="return">退货入库</a-select-option>
                <a-select-option value="production">生产入库</a-select-option>
                <a-select-option value="other">其他入库</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="记录日期" name="recordDate" required>
              <a-date-picker
                v-model:value="form.recordDate"
                placeholder="请选择记录日期"
                style="width: 100%"
                format="YYYY-MM-DD"
                :disabled-date="disabledDate"
              />
            </a-form-item>
          </a-col>
          <a-col :span="8">
            <a-form-item label="备注" name="remark">
              <a-input
                v-model:value="form.remark"
                placeholder="请输入备注（可选）"
                maxlength="200"
              />
            </a-form-item>
          </a-col>
        </a-row>
      </a-card>
      
      <a-card title="入库商品" class="form-card">
        <!-- 商品选择区域 -->
        <div class="product-selection">
          <a-row :gutter="16">
            <a-col :span="6">
              <a-form-item label="选择分类">
                <a-tree-select
                  v-model:value="selectedCategoryId"
                  :tree-data="categoryOptions"
                  :field-names="{ label: 'name', value: 'id', children: 'children' }"
                  placeholder="请选择商品分类"
                  @change="onCategoryChange"
                  style="width: 100%"
                  :loading="categoriesLoading"
                  :disabled="categoriesLoading"
                  allowClear
                />
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="选择商品" required>
                <a-select
                  v-if="!selectedProductId"
                  v-model:value="selectedProductId"
                  placeholder="请选择商品"
                  @change="onProductSelect"
                  style="width: 100%"
                  :loading="productsLoading"
                  :disabled="productsLoading || !availableProducts.length"
                  showSearch
                  :filter-option="filterProductOption"
                  allowClear
                >
                  <a-select-option
                    v-for="product in availableProducts"
                    :key="product.id"
                    :value="product.id"
                    :label="`${product.name} (¥${product.salePrice || 0})`"
                  >
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <img
                        v-if="product.firstImageUrl"
                        :src="product.firstImageUrl"
                        style="width: 24px; height: 24px; object-fit: cover; border-radius: 2px; flex-shrink: 0;"
                        @error="product.firstImageUrl = ''"
                        @mouseenter="showDropdownPreview($event, product.firstImageUrl)"
                        @mouseleave="hideDropdownPreview"
                      />
                      <div
                        v-else
                        style="width: 24px; height: 24px; background: #f5f5f5; border-radius: 2px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #ccc; font-size: 12px; border: 1px solid #e8e8e8;"
                      >
                        <span>图</span>
                      </div>
                      <span>{{ product.name }} (¥{{ product.salePrice || 0 }})</span>
                    </div>
                  </a-select-option>
                </a-select>

                <!-- 已选中商品显示 -->
                <div v-else
                     @click="handleProductReselect"
                     style="cursor: pointer; padding: 8px 12px; background: #f5f5f5; border-radius: 6px; border: 1px solid #d9d9d9; transition: background-color 0.2s;"
                     @mouseenter="$event.target.style.backgroundColor = '#e6f7ff'"
                     @mouseleave="$event.target.style.backgroundColor = '#f5f5f5'"
                >
                  {{ currentProductName }}
                </div>

                <div v-if="productsLoading" style="text-align: center; margin: 20px 0;">
                  <a-spin size="large" />
                  <div style="margin-top: 12px; color: #666;">正在加载商品数据...</div>
                </div>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="选择仓库" required>
                <a-select
                  v-model:value="selectedWarehouseId"
                  placeholder="请选择仓库"
                  @change="onWarehouseChange"
                  style="width: 100%"
                >
                  <a-select-option
                    v-for="warehouse in warehouses"
                    :key="warehouse.id"
                    :value="warehouse.id"
                  >
                    {{ warehouse.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :span="6">
              <a-form-item label="&nbsp;">
                <a-button
                  type="primary"
                  @click="addProductToList"
                  :disabled="!selectedProductId || !selectedWarehouseId || availableSkus.length === 0"
                  style="width: 100%"
                >
                  添加到列表
                </a-button>
              </a-form-item>
            </a-col>
          </a-row>


        </div>
        
        <!-- 当前选择商品的SKU -->
        <div v-if="!selectedProductId || !selectedWarehouseId" class="empty-sku">
          <a-empty description="请先选择商品和仓库" />
        </div>
        
        <div v-else-if="availableSkus.length === 0" class="empty-sku">
          <a-empty description="该商品暂无SKU信息" />
        </div>
        
        <div v-else class="sku-section">
          <h4 style="margin-bottom: 12px;">选择要入库的SKU</h4>
          <div style="border: 1px solid #d9d9d9; border-radius: 6px; padding: 16px; background: #fafafa;">
            <!-- 表头 -->
            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px; font-weight: 500; color: #666;">
              <div style="text-align: center;">颜色</div>
              <div style="text-align: center;">尺码</div>
              <div style="text-align: center;">现库存</div>
              <div style="text-align: center;">入库数量</div>
              <div style="text-align: center;">操作后</div>
            </div>
            
            <!-- SKU选项列表 -->
            <div style="max-height: 300px; overflow-y: auto;">
              <div
                v-for="sku in availableSkus"
                :key="sku.key"
                :class="['sku-row']"
                style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 12px; padding: 8px; border-radius: 4px; margin-bottom: 4px; align-items: center; background: white;"
              >
                <div style="text-align: center; font-weight: 500;">{{ sku.color }}</div>
                <div style="text-align: center; font-weight: 500;">{{ sku.size }}</div>
                <div style="text-align: center;">
                  <a-tag :color="sku.stockQuantity > 10 ? 'green' : sku.stockQuantity > 0 ? 'orange' : 'red'" style="margin: 0;">
                    {{ sku.stockQuantity }}
                  </a-tag>
                </div>
                <div style="text-align: center;">
                  <a-input-number
                    v-model:value="sku.operationQuantity"
                    :min="1"
                    size="small"
                    style="width: 80px;"
                    @change="updateSkuFinalQuantity(sku)"
                  />
                </div>
                <div style="text-align: center;">
                  <a-tag 
                    v-if="sku.operationQuantity && sku.operationQuantity > 0"
                    :color="sku.finalQuantity >= 0 ? (sku.finalQuantity > 10 ? 'green' : sku.finalQuantity > 0 ? 'orange' : 'red') : 'red'"
                    style="margin: 0;"
                  >
                    {{ sku.finalQuantity }}
                  </a-tag>
                  <span v-else style="color: #999;">-</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 已添加到批量入库列表的商品 -->
        <div v-if="batchItems.length > 0" class="batch-list">
          <div style="display: flex; justify-content: space-between; align-items: center; margin: 24px 0 12px 0;">
            <h4 style="margin: 0;">批量入库列表 ({{ batchItems.length }}个商品)</h4>
            <a-button type="link" danger @click="clearBatchList">清空列表</a-button>
          </div>
          
          <div style="border: 1px solid #d9d9d9; border-radius: 6px; padding: 16px;">
            <!-- 表头 -->
            <div style="display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 120px; gap: 12px; margin-bottom: 12px; font-weight: 500; color: #666;">
              <div>商品名称</div>
              <div style="text-align: center;">仓库</div>
              <div style="text-align: center;">颜色</div>
              <div style="text-align: center;">尺码</div>
              <div style="text-align: center;">入库数量</div>
              <div style="text-align: center;">单价</div>
              <div style="text-align: center;">操作</div>
            </div>
            
            <!-- 批量入库项目列表 -->
            <div style="max-height: 400px; overflow-y: auto;">
              <div
                v-for="(item, index) in batchItems"
                :key="item.key"
                style="display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 120px; gap: 12px; padding: 8px; border-radius: 4px; margin-bottom: 4px; align-items: center; border: 1px solid #f0f0f0;"
              >
                <div style="font-weight: 500; display: flex; align-items: center; gap: 8px;" class="product-name-cell">
                  <div v-if="item.imageUrl" class="product-thumb-wrapper">
                    <img :src="$thumb(item.imageUrl)" class="product-thumb" @error="item.imageUrl = ''" />
                    <div class="product-preview">
                      <img :src="item.imageUrl" class="product-preview-img" />
                    </div>
                  </div>
                  <span>{{ item.productName }}</span>
                </div>
                <div style="text-align: center;">{{ item.warehouseName }}</div>
                <div style="text-align: center;">{{ item.color }}</div>
                <div style="text-align: center;">{{ item.size }}</div>
                <div style="text-align: center;">
                  <a-input-number
                    v-if="item.editing"
                    v-model:value="item.quantity"
                    :min="1"
                    size="small"
                    style="width: 80px;"
                    @blur="stopEditItem(index)"
                    @pressEnter="stopEditItem(index)"
                    ref="editInput"
                  />
                  <span v-else>{{ item.quantity }}</span>
                </div>
                <div style="text-align: center;">
                  <span>{{ item.unitPrice || '-' }}</span>
                </div>
                <div style="text-align: center;">
                  <a-button 
                    v-if="!item.editing"
                    type="link" 
                    size="small" 
                    @click="startEditItem(index)"
                    style="margin-right: 4px;"
                  >
                    编辑
                  </a-button>
                  <a-button 
                    v-if="item.editing"
                    type="link" 
                    size="small" 
                    @click="stopEditItem(index)"
                    style="margin-right: 4px;"
                  >
                    确认
                  </a-button>
                  <a-button type="link" danger size="small" @click="removeBatchItem(index)">删除</a-button>
                </div>
              </div>
            </div>
            
            <!-- 合计行 -->
            <div 
              v-if="batchItems.length > 0"
              style="display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 120px; gap: 12px; padding: 12px 8px; border-radius: 4px; align-items: center; background: #f8f9fa; border: 2px solid #e6f7ff; border-top: 2px solid #1890ff; margin-top: 8px;"
            >
              <div style="font-weight: 600; color: #1890ff;">合计</div>
              <div></div>
              <div></div>
              <div></div>
              <div style="text-align: center; font-weight: 600; color: #1890ff;">
                {{ totalQuantity }}
              </div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
      </a-card>
      
      <div class="form-actions">
        <a-button type="primary" html-type="submit" size="large" :loading="loading">
          确认入库
        </a-button>
        <a-button size="large" @click="resetForm" style="margin-left: 16px">
          重置
        </a-button>
      </div>
    </a-form>

    <!-- 下拉预览大图 -->
    <div
      v-if="dropdownPreview.visible"
      :style="{
        position: 'fixed',
        left: dropdownPreview.x + 'px',
        top: dropdownPreview.y + 'px',
        zIndex: 9999,
        padding: '4px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        pointerEvents: 'none'
      }"
    >
      <img :src="dropdownPreview.url" style="width: 200px; height: 200px; object-fit: cover; border-radius: 6px;" />
    </div>
  </div>
</template>

<script>
import { reactive, ref, onMounted, computed } from 'vue'
import { message } from 'ant-design-vue'
import dayjs from 'dayjs'
import { stockApi, productApi, warehouseApi, categoryApi } from '@/api'


export default {
  name: 'StockIn',
  components: {
  },
  setup() {
    const loading = ref(false)
    const productsLoading = ref(false)
    const dropdownPreview = reactive({ visible: false, url: '', x: 0, y: 0 })

    const showDropdownPreview = (e, url) => {
      if (!url) return
      const rect = e.target.getBoundingClientRect()
      dropdownPreview.url = url
      dropdownPreview.x = rect.right + 10
      dropdownPreview.y = rect.top - 90
      dropdownPreview.visible = true
    }
    const hideDropdownPreview = () => {
      dropdownPreview.visible = false
    }
    const categoriesLoading = ref(false)
    const warehouses = ref([])
    const colorOptions = ref([])
    const sizeOptions = ref([])
    const categoryOptions = ref([])
    const availableProducts = ref([])
    const selectedCategoryId = ref(undefined)
    const selectedProductId = ref(undefined)
    const selectedWarehouseId = ref(undefined)
    const availableSkus = ref([])
    const currentProduct = ref(null)
    const batchItems = ref([])
    
    const form = reactive({
      reasonType: 'return',
      recordDate: dayjs(),
      remark: '',
      items: []
    })
    
    const loadCategories = async () => {
      categoriesLoading.value = true
      try {
        const result = await categoryApi.getTree()
        categoryOptions.value = result.data || []
      } catch (error) {
        console.error('加载分类失败:', error)
        categoryOptions.value = []
      } finally {
        categoriesLoading.value = false
      }
    }

    const loadProducts = async (categoryId = null, searchQuery = '') => {
      productsLoading.value = true
      try {
        const queryParams = {
          status: 1
        }

        if (searchQuery) {
          queryParams.name = searchQuery.trim()
        }

        if (categoryId) {
          queryParams.categoryId = categoryId
        }

        const result = await productApi.querySimpleInfo(queryParams)
        availableProducts.value = result.data || []
      } catch (error) {
        console.error('加载商品失败:', error)
        availableProducts.value = []
      } finally {
        productsLoading.value = false
      }
    }
    

    const onCategoryChange = (categoryId) => {
      selectedCategoryId.value = categoryId
      selectedProductId.value = undefined
      currentProduct.value = null
      availableSkus.value = []

      // 根据选择的分类加载商品
      loadProducts(categoryId)
    }

    const clearProductSelection = () => {
      selectedProductId.value = undefined
      currentProduct.value = null
      availableSkus.value = []
    }

    // 处理商品重新选择
    const handleProductReselect = () => {
      clearProductSelection()
    }

    // 商品搜索过滤
    const filterProductOption = (input, option) => {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    const onProductSelect = async (productId) => {
      if (!productId) {
        clearProductSelection()
        return
      }

      // 从可用商品列表中找到选中的商品
      const product = availableProducts.value.find(p => p.id === productId)
      if (!product) {
        console.error('未找到选中的商品')
        return
      }

      // 设置选中的商品
      selectedProductId.value = productId
      currentProduct.value = { ...product }

      // 清空之前的SKU数据
      availableSkus.value = []

      // 如果已选择仓库，立即加载商品详情和SKU
      if (selectedWarehouseId.value) {
        await loadProductDetail(productId)
        loadAvailableSkus(selectedWarehouseId.value)
      } else {
        // 即使没选择仓库，也先加载商品详情
        await loadProductDetail(productId)
      }
    }
    
    const loadWarehouses = async () => {
      try {
        const result = await warehouseApi.list()
        warehouses.value = result.data || []

        // 如果只有一个仓库，自动选中
        if (warehouses.value.length === 1) {
          selectedWarehouseId.value = warehouses.value[0].id
        }
      } catch (error) {
        console.error('加载仓库失败:', error)
      }
    }

    
    const loadAttributes = async () => {
      try {
        const [colors, sizes] = await Promise.all([
          productApi.getAttributes('color'),
          productApi.getAttributes('size')
        ])
        colorOptions.value = colors.data
        sizeOptions.value = sizes.data
      } catch (error) {
        console.error('加载属性失败:', error)
      }
    }
    
    const disabledDate = (current) => {
      return current && current > dayjs().endOf('day')
    }
    
    const formatRecordDate = (date) => {
      return date ? dayjs(date).format('YYYYMMDD') : ''
    }
    
    const loadProductDetail = async (productId) => {
      try {
        const result = await productApi.detail(productId)
        if (result.data) {
          currentProduct.value = { ...result.data }
        }
        return result.data
      } catch (error) {
        console.error('加载商品详情失败:', error)
        return null
      }
    }
    
    const loadAvailableSkus = (warehouseId) => {
      if (!currentProduct.value || !warehouseId) {
        availableSkus.value = []
        return
      }
      
      // 从商品的stockItems中筛选出当前仓库的SKU
      const skus = currentProduct.value.stockItems?.filter(
        item => item.warehouseId === warehouseId
      ) || []
      
      availableSkus.value = skus.map(item => ({
        key: `${item.warehouseId}-${item.color || 'default'}-${item.size || 'default'}`,
        color: item.color || '默认颜色',
        size: item.size || '默认尺码',
        stockQuantity: parseInt(item.stockQuantity) || 0,
        warehouseId: item.warehouseId,
        operationQuantity: undefined,
        finalQuantity: 0
      }))
    }
    
    const onWarehouseChange = async (warehouseId) => {
      // 如果已经选择了商品，重新加载商品详情和SKU
      if (selectedProductId.value && currentProduct.value) {
        await loadProductDetail(selectedProductId.value)
        loadAvailableSkus(warehouseId)
      } else {
        // 清空SKU数据
        availableSkus.value = []
      }
    }
    
    const updateSkuFinalQuantity = (sku) => {
      if (!sku.operationQuantity || sku.operationQuantity <= 0) {
        sku.finalQuantity = sku.stockQuantity
        return
      }
      
      const currentStock = parseInt(sku.stockQuantity) || 0
      const quantity = parseInt(sku.operationQuantity) || 0
      
      sku.finalQuantity = currentStock + quantity
    }
    
    const addProductToList = () => {
      if (!selectedProductId.value || !selectedWarehouseId.value || !currentProduct.value) {
        message.error('请先选择商品和仓库')
        return
      }

      // 检查是否有SKU输入了数量
      const operationSkus = availableSkus.value.filter(sku => sku.operationQuantity && sku.operationQuantity > 0)
      
      if (operationSkus.length === 0) {
        message.error('请至少为一个SKU输入入库数量')
        return
      }

      const selectedWarehouse = warehouses.value.find(w => w.id === selectedWarehouseId.value)
      
      operationSkus.forEach(sku => {
        const key = `${selectedProductId.value}-${selectedWarehouseId.value}-${sku.color}-${sku.size}`
        
        // 检查是否已经存在相同的商品SKU
        const existingIndex = batchItems.value.findIndex(item => item.key === key)
        
        if (existingIndex >= 0) {
          // 如果已存在，更新数量
          batchItems.value[existingIndex].quantity = sku.operationQuantity
          message.info(`已更新 ${currentProduct.value.name} (${sku.color}/${sku.size}) 的入库数量`)
        } else {
          // 如果不存在，添加新项
          batchItems.value.push({
            key,
            productId: selectedProductId.value,
            productName: currentProduct.value.name,
            imageUrl: currentProduct.value.imageUrls?.[0] || '',
            warehouseId: selectedWarehouseId.value,
            warehouseName: selectedWarehouse?.name || '',
            color: sku.color,
            size: sku.size,
            quantity: sku.operationQuantity,
            unitPrice: currentProduct.value.salePrice || undefined
          })
        }
      })

      // 清空当前选择
      availableSkus.value.forEach(sku => {
        sku.operationQuantity = undefined
        sku.finalQuantity = sku.stockQuantity
      })
      
      message.success(`已添加 ${operationSkus.length} 个SKU到批量入库列表`)
    }

    const removeBatchItem = (index) => {
      batchItems.value.splice(index, 1)
      message.success('已从批量入库列表中移除')
    }

    const clearBatchList = () => {
      batchItems.value = []
      message.success('已清空批量入库列表')
    }

    const startEditItem = (index) => {
      batchItems.value[index].editing = true
    }

    const stopEditItem = (index) => {
      batchItems.value[index].editing = false
    }

    const totalQuantity = computed(() => {
      return batchItems.value.reduce((sum, item) => sum + (item.quantity || 0), 0)
    })

    // 添加计算属性来确保响应式更新
    const currentProductName = computed(() => {
      return currentProduct.value?.name || '未知商品'
    })

    const resetForm = () => {
      form.reasonType = 'return'
      form.recordDate = dayjs()
      form.remark = ''
      selectedCategoryId.value = undefined
      selectedProductId.value = undefined
      selectedWarehouseId.value = undefined
      availableSkus.value = []
      availableProducts.value = []
      currentProduct.value = null
      batchItems.value = []
    }
    
    const handleSubmit = async () => {
      if (!form.recordDate) {
        message.error('请选择记录日期')
        return
      }
      
      // 检查批量入库列表是否为空
      if (batchItems.value.length === 0) {
        message.error('请至少添加一个商品到批量入库列表')
        return
      }
      
      // 验证所有批量项目的数量
      const invalidItems = batchItems.value.filter(item => !item.quantity || item.quantity <= 0)
      if (invalidItems.length > 0) {
        message.error('请为所有商品设置有效的入库数量')
        return
      }
      
      loading.value = true
      try {
        // 构建批量入库请求数据
        const requestData = {
          movementType: 'IN',
          reasonType: form.reasonType,
          recordDate: formatRecordDate(form.recordDate),
          remark: form.remark,
          items: batchItems.value.map(item => ({
            productId: item.productId,
            warehouseId: item.warehouseId,
            color: item.color,
            size: item.size,
            quantity: item.quantity,
            unitPrice: item.unitPrice
          }))
        }
        
        await stockApi.batch(requestData)
        
        const totalQuantity = batchItems.value.reduce((sum, item) => sum + item.quantity, 0)
        message.success(`批量入库成功 - 共${batchItems.value.length}个SKU，总数量：${totalQuantity}`)
        
        resetForm()
      } catch (error) {
        console.error('批量入库失败:', error)
        message.error(`批量入库失败: ${error.message || '未知错误'}`)
      } finally {
        loading.value = false
      }
    }
    
    onMounted(async () => {
      await loadWarehouses()
      await loadAttributes()
      await loadCategories() // 加载分类
      await loadProducts() // 初始加载所有商品
    })
    
    return {
      form,
      loading,
      productsLoading,
      categoriesLoading,
      warehouses,
      colorOptions,
      sizeOptions,
      categoryOptions,
      availableProducts,
      selectedCategoryId,
      selectedProductId,
      selectedWarehouseId,
      availableSkus,
      batchItems,
      loadProducts,
      onCategoryChange,
      clearProductSelection,
      handleProductReselect,
      filterProductOption,
      onProductSelect,
      onWarehouseChange,
      updateSkuFinalQuantity,
      addProductToList,
      removeBatchItem,
      clearBatchList,
      startEditItem,
      stopEditItem,
      totalQuantity,
      currentProductName,
      resetForm,
      handleSubmit,
      disabledDate,
      formatRecordDate,
      dropdownPreview,
      showDropdownPreview,
      hideDropdownPreview
    }
  }
}
</script>

<style scoped>
.stock-in {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.form-card {
  margin-bottom: 16px;
}

.product-selection {
  margin-bottom: 24px;
}

.batch-list {
  margin-top: 24px;
}

.empty-sku {
  padding: 40px 0;
  text-align: center;
}

.sku-section {
  margin-top: 16px;
}

.sku-row {
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.sku-row:hover {
  background-color: #f5f5f5;
  border-color: #d9d9d9;
}

.form-actions {
  text-align: center;
  padding: 24px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 32px;
}

.product-thumb-wrapper {
  position: relative;
  flex-shrink: 0;
}

.product-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  cursor: pointer;
}

.product-preview {
  display: none;
  position: absolute;
  left: 40px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
  padding: 4px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.product-thumb-wrapper:hover .product-preview {
  display: block;
}

.product-preview-img {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 6px;
}
</style>